import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { CompaniesService } from '../api/companies.service';
import { UserProfileService } from '../services/user-profile.service';
import { CustomCompanyService } from '../services/company.service';
import { ThemeService } from '../services/theme.service';
 
@Injectable({ providedIn: 'root' })
export class AuthService {
  private _userProfile: any;
  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject$.asObservable();
 
  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  public readonly companyLoadFailed$ = new BehaviorSubject<boolean>(false);
 
  private buildPermissionsStructure(functions: any[]): any {
    return functions.map(func => ({
      id: func.id,
      permissions: func.permissions
    }));
  }
 
  /**
   * Publishes `true` if and only if (a) all the asynchronous initial
   * login calls have completed or errorred, and (b) the user ended up
   * being authenticated.
   *
   * In essence, it combines:
   *
   * - the latest known state of whether the user is authorized
   * - whether the ajax calls for initial log in have all been done
   */
  public canActivateProtectedRoutes$: Observable<boolean> = combineLatest([
    this.isAuthenticated$,
    this.isDoneLoading$,
  ]).pipe(map((values) => values.every((b) => b)));
 
  private navigateToLoginPage() {
    // TODO: Remember current URL
    this.router.navigateByUrl('/');
  }
 
  constructor(private oauthService: OAuthService, private router: Router, private companiesService: CompaniesService, private userProfileService: UserProfileService, private companyService: CustomCompanyService, private themeService: ThemeService) {
    // Useful for debugging:
    this.oauthService.events.subscribe((event) => {
      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
      } else {
        //console.warn('OAuthEvent Object:', event);
      }
    });
 
    // This is tricky, as it might cause race conditions (where access_token is set in another
    // tab before everything is said and done there.
    // TODO: Improve this setup. See: https://github.com/jeroenheijmans/sample-angular-oauth2-oidc-with-auth-guards/issues/2
    window.addEventListener('storage', (event) => {
      if (event.key === 'company-switch') {
        this.handleCompanySwitchFromAnotherTab();
        return;
      }
 
      // The `key` is `null` if the event was caused by `.clear()`
      if (event.key !== 'access_token' && event.key !== null) {
        return;
      }
 
      console.warn('Noticed changes to access_token (most likely from another tab), updating isAuthenticated');
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
 
      if (!this.oauthService.hasValidAccessToken()) {
        this.navigateToLoginPage();
      }
    });
 
    this.oauthService.events.subscribe((_) => {
      this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
    });
    this.isAuthenticatedSubject$.next(this.oauthService.hasValidAccessToken());
 
    this.oauthService.events
      .pipe(filter((e) => ['token_received'].includes(e.type)))
      .subscribe((e) => this.oauthService.loadUserProfile());
 
    this.oauthService.events
      .pipe(filter((e) => ['session_terminated', 'session_error'].includes(e.type)))
      .subscribe((e) => this.navigateToLoginPage());
 
    this.oauthService.setupAutomaticSilentRefresh();
  }
 
  public runInitialLoginSequence(): Promise<void> {
    if (location.hash) {
      console.log('Encountered hash fragment, plotting as table...');
      console.table(
        location.hash
          .substr(1)
          .split('&')
          .map((kvp) => kvp.split('='))
      );
    }
 
    // 0. LOAD CONFIG:
    // First we have to check to see how the IdServer is
    // currently configured:
    return (
      this.oauthService
        .loadDiscoveryDocument()
 
        // For demo purposes, we pretend the previous call was very slow
        //.then(() => new Promise<void>((resolve) => setTimeout(() => resolve(), 1500)))
 
        // 1. HASH LOGIN:
        // Try to log in via hash fragment after redirect back
        // from IdServer from initImplicitFlow:
        .then(() => this.oauthService.tryLogin())
 
        .then(async () => {
          try {
            const profile = await this.oauthService.loadUserProfile();
 
            this._userProfile = profile;
            sessionStorage.setItem('userProfile', JSON.stringify(profile));
 
            let companyProfile = this.companyService.getCompanyProfile();
            if (!companyProfile) {
              try {
                const idFake = '00000000-0000-0000-0000-000000000000';
                const company: any = await firstValueFrom(this.companiesService.v1CompaniesApiCompaniesCompanyIdGet(idFake));
                this.companyService.setCompanyProfile(company);
                companyProfile = company;
               
                // Cargar tema desde el perfil de la empresa
                if (company?.styleSettings) {
                  this.themeService.loadThemeFromCompanyProfile(company.styleSettings);
                }
              } catch (err) {
                console.error('Error loading company profile from API', err);
                this.companyLoadFailed$.next(true);
              }
            } else {
              // Si ya existe el perfil, aplicar el tema guardado
              const styleSettings = this.companyService.getStyleSettings();
              if (styleSettings) {
                this.themeService.loadThemeFromCompanyProfile(styleSettings);
              }
            }
 
            let userProfile = this.userProfileService.getProfile();
            if (!userProfile) {
              try {
                const userProfileFromApi = await firstValueFrom(this.companiesService.companiesGetUserProfile());
                this.userProfileService.setProfile({
                  ...userProfileFromApi,
                  enabledLanguages: companyProfile?.languages || []
                });
              } catch (err) {
                console.error('Error loading user profile data from API', err);
              }
            }
 
            let userModules = this.userProfileService.getUserModules();
            if (userModules.length === 0) {
              try {
                const userModulesFromApi = await firstValueFrom(this.companiesService.companiesGetUserRoleAssignment());
                this.userProfileService.setUserModules(userModulesFromApi.accesses);
              } catch (err) {
                console.error('Error loading user modules', err);
              }
            }
 
          } catch (error) {
            console.error('Error loading user info', error);
          }
        })
        .then(() => {
          this.isDoneLoadingSubject$.next(true);
 
          // Check for the strings 'undefined' and 'null' just to be sure. Our current
          // login(...) should never have this, but in case someone ever calls
          // initImplicitFlow(undefined | null) this could happen.
          if (
            this.oauthService.state &&
            this.oauthService.state !== 'undefined' &&
            this.oauthService.state !== 'null'
          ) {
            let stateUrl = this.oauthService.state;
            if (stateUrl.startsWith('/') === false) {
              stateUrl = decodeURIComponent(stateUrl);
            }
            console.log(`There was state of ${this.oauthService.state}, so we are sending you to: ${stateUrl}`);
            this.router.navigateByUrl(stateUrl);
          }
        })
        .catch(() => this.isDoneLoadingSubject$.next(true))
    );
  }
 
  public login(targetUrl?: string) {
    // Note: before version 9.1.0 of the library you needed to
    // call encodeURIComponent on the argument to the method.
    this.oauthService.initLoginFlow(targetUrl || this.router.url);
  }
 
  public logout() {
    this._userProfile = null;
    this.isAuthenticatedSubject$.next(false);
    this.companyLoadFailed$.next(false);
    sessionStorage.clear();
    this.oauthService.logOut();
  }
  public refresh() {
    this.oauthService.silentRefresh();
  }
  public hasValidToken() {
    return this.oauthService.hasValidAccessToken();
  }
 
  // These normally won't be exposed from a service like this, but
  // for debugging it makes sense.
  public get accessToken() {
    return this.oauthService.getAccessToken();
  }
  public get refreshToken() {
    return this.oauthService.getRefreshToken();
  }
  public get identityClaims() {
    return this.oauthService.getIdentityClaims();
  }
  public get idToken() {
    return this.oauthService.getIdToken();
  }
  public get logoutUrl() {
    return this.oauthService.logoutUrl;
  }
  get userProfileData() {
    if (!this._userProfile) {
      this._userProfile = JSON.parse(sessionStorage.getItem('userProfile') || 'null');
    }
    return this._userProfile;
  }
 
  public userHasProfile(): boolean {
    const perfil = this.userProfileService.getProfile();
    return !!perfil;
    //return false;
  }
  public userHasBeenOnboarded(): boolean {
    const perfil = this.userProfileService.getProfile();
    return perfil && perfil.isOnboarded ? perfil.isOnboarded : false
    //return false;
  }
 
  public get companyProfile(): any {
    return this.companyService.getCompanyProfile();
  }
 
  public get appUserProfile(): any {
    return this.userProfileService.getProfile();
  }
 
  public get userProfile(): any {
    return this.userProfileData;
  }
 
  private async handleCompanySwitchFromAnotherTab() {
    try {
      this.themeService.resetTheme();
      this.companyService.clearCompanyProfile();
      this.userProfileService.clearUserModules();
 
      // Limpia tokens pero mantiene la sesion en el proveedor de identidad
      this.oauthService.logOut(false);
      await this.oauthService.loadDiscoveryDocument();
      this.oauthService.initCodeFlow('/home');
    } catch (error) {
      console.error('Error handling company switch from another tab', error);
      this.oauthService.initCodeFlow('/home');
    }
  }
}
